import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Check, RotateCcw, Volume2 } from "lucide-react";
import { earnXP } from "../gamification";
import lessons from "../data/lessons.json";
import vocabData from "../data/vocabulary.json";
import { VowelCatcher, ArticleQuiz, AnimalSentence, SoundMatch, SpellingBee, WordHunt } from "../games/KidGames";

const allWords = [];
vocabData.forEach((cat) => cat.words.forEach((w) => allWords.push(w)));

function shuffle(arr) { return [...arr].sort(() => Math.random() - 0.5); }

function speak(text) {
  if ("speechSynthesis" in window) {
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 0.85; u.lang = "en-US";
    window.speechSynthesis.speak(u);
  }
}

// Game 1: Word Match — match word to meaning
function WordMatch({ onFinish }) {
  const [pairs, setPairs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [matched, setMatched] = useState(new Set());
  const [wrong, setWrong] = useState(null);
  const [score, setScore] = useState(0);

  useEffect(() => {
    const picked = shuffle(allWords).slice(0, 5);
    const words = picked.map((w, i) => ({ id: `w${i}`, text: w.word, pairId: i, type: "word" }));
    const meanings = picked.map((w, i) => ({ id: `m${i}`, text: w.meaning, pairId: i, type: "meaning" }));
    setPairs(shuffle([...words, ...shuffle(meanings)]));
  }, []);

  function handleSelect(item) {
    if (matched.has(item.pairId) && matched.has(item.pairId + "done")) return;
    if (!selected) { setSelected(item); return; }
    if (selected.id === item.id) { setSelected(null); return; }
    if (selected.type === item.type) { setSelected(item); return; }
    if (selected.pairId === item.pairId) {
      setMatched((prev) => new Set([...prev, `${item.pairId}w`, `${item.pairId}m`]));
      setScore((s) => s + 1);
      setSelected(null);
      if (matched.size / 2 + 1 >= 5) setTimeout(() => onFinish(score + 1), 500);
    } else {
      setWrong(item.id);
      setTimeout(() => { setWrong(null); setSelected(null); }, 600);
    }
  }

  return (
    <div>
      <h3 className="font-bold text-gray-900 mb-1">Match Word to Meaning</h3>
      <p className="text-sm text-gray-400 mb-4">Tap a word, then tap its meaning</p>
      <div className="grid grid-cols-2 gap-2">
        {pairs.map((item) => {
          const isMatched = matched.has(`${item.pairId}${item.type[0]}`);
          const isSelected = selected?.id === item.id;
          const isWrong = wrong === item.id;
          return (
            <button key={item.id} onClick={() => !isMatched && handleSelect(item)}
              className={`p-3 rounded-xl text-sm font-medium transition-all border-2 text-left ${
                isMatched ? "bg-sage-light border-sage-light text-sage-dark opacity-60" :
                isWrong ? "bg-berry-light border-berry-light text-berry animate-pulse" :
                isSelected ? "bg-paper-deep border-gold-500 text-gold-700" :
                "bg-white border-gray-100 text-gray-700 hover:border-gray-300"
              }`}>
              {item.text}
            </button>
          );
        })}
      </div>
      <p className="text-center text-sm text-gray-400 mt-3">{score}/5 matched</p>
    </div>
  );
}

// Game 2: Unscramble — rearrange letters to form a word
function Unscramble({ onFinish }) {
  const [words] = useState(() => shuffle(allWords).slice(0, 5).map((w) => w.word.toLowerCase()));
  const [current, setCurrent] = useState(0);
  const [input, setInput] = useState("");
  const [score, setScore] = useState(0);
  const [feedback, setFeedback] = useState(null);

  const scrambled = words[current] ? shuffle(words[current].split("")).join("") : "";

  function checkAnswer() {
    if (input.toLowerCase().trim() === words[current]) {
      setScore((s) => s + 1);
      setFeedback("correct");
    } else {
      setFeedback("wrong");
    }
    setTimeout(() => {
      setFeedback(null); setInput("");
      if (current < words.length - 1) setCurrent((c) => c + 1);
      else onFinish(score + (input.toLowerCase().trim() === words[current] ? 1 : 0));
    }, 800);
  }

  return (
    <div>
      <h3 className="font-bold text-gray-900 mb-1">Unscramble the Word</h3>
      <p className="text-sm text-gray-400 mb-4">Rearrange the letters ({current + 1}/5)</p>
      <div className="bg-gray-50 rounded-xl p-6 text-center mb-4">
        <p className="text-2xl font-bold text-gold-700 tracking-widest">{scrambled.toUpperCase()}</p>
      </div>
      <div className="flex gap-2">
        <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && checkAnswer()}
          placeholder="Type the word..." className={`flex-1 px-4 py-3 border-2 rounded-xl text-sm focus:outline-none ${feedback === "correct" ? "border-sage bg-sage-light" : feedback === "wrong" ? "border-berry-light bg-berry-light" : "border-gray-200 focus:border-gold-500"}`} />
        <button onClick={checkAnswer} className="bg-ink-900 text-ink-900 px-5 py-3 rounded-xl font-semibold hover:bg-ink-900 transition-all">
          <Check className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

// Game 3: Listen & Type
function ListenType({ onFinish }) {
  const [sentences] = useState(() => shuffle(lessons).slice(0, 5).map((l) => l.sentence));
  const [current, setCurrent] = useState(0);
  const [input, setInput] = useState("");
  const [score, setScore] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [revealed, setRevealed] = useState(false);

  function playAudio() { speak(sentences[current]); }

  function checkAnswer() {
    const clean = (s) => s.toLowerCase().replace(/[^a-z\s]/g, "").trim();
    const correct = clean(input) === clean(sentences[current]);
    if (correct) setScore((s) => s + 1);
    setFeedback(correct ? "correct" : "wrong");
    setRevealed(true);
    setTimeout(() => {
      setFeedback(null); setInput(""); setRevealed(false);
      if (current < sentences.length - 1) setCurrent((c) => c + 1);
      else onFinish(score + (correct ? 1 : 0));
    }, 1500);
  }

  return (
    <div>
      <h3 className="font-bold text-gray-900 mb-1">Listen & Type</h3>
      <p className="text-sm text-gray-400 mb-4">Listen to the sentence, then type it ({current + 1}/5)</p>
      <button onClick={playAudio} className="w-full bg-paper-deep border border-ink-100 rounded-xl p-6 flex items-center justify-center gap-3 mb-4 hover:bg-cream-100 transition-all">
        <Volume2 className="w-8 h-8 text-gold-700" />
        <span className="text-gold-700 font-semibold">Play Audio</span>
      </button>
      {revealed && <p className="text-sm text-center mb-2 font-medium text-gray-600">Answer: "{sentences[current]}"</p>}
      <div className="flex gap-2">
        <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && checkAnswer()}
          placeholder="Type what you heard..." className={`flex-1 px-4 py-3 border-2 rounded-xl text-sm focus:outline-none ${feedback === "correct" ? "border-sage bg-sage-light" : feedback === "wrong" ? "border-berry-light bg-berry-light" : "border-gray-200 focus:border-gold-500"}`} />
        <button onClick={checkAnswer} className="bg-ink-900 text-ink-900 px-5 py-3 rounded-xl font-semibold hover:bg-ink-900 transition-all">
          <Check className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

// Game 4: Fill in the Blank
function FillBlank({ onFinish }) {
  const [questions] = useState(() => {
    return shuffle(lessons).slice(0, 5).map((l) => {
      const words = l.sentence.split(" ");
      const idx = Math.floor(Math.random() * words.length);
      const answer = words[idx];
      const blanked = words.map((w, i) => i === idx ? "____" : w).join(" ");
      return { blanked, answer: answer.toLowerCase().replace(/[^a-z]/g, ""), full: l.sentence };
    });
  });
  const [current, setCurrent] = useState(0);
  const [input, setInput] = useState("");
  const [score, setScore] = useState(0);
  const [feedback, setFeedback] = useState(null);

  function checkAnswer() {
    const correct = input.toLowerCase().trim() === questions[current].answer;
    if (correct) setScore((s) => s + 1);
    setFeedback(correct ? "correct" : "wrong");
    setTimeout(() => {
      setFeedback(null); setInput("");
      if (current < questions.length - 1) setCurrent((c) => c + 1);
      else onFinish(score + (correct ? 1 : 0));
    }, 800);
  }

  return (
    <div>
      <h3 className="font-bold text-gray-900 mb-1">Fill in the Blank</h3>
      <p className="text-sm text-gray-400 mb-4">Complete the sentence ({current + 1}/5)</p>
      <div className="bg-gray-50 rounded-xl p-6 text-center mb-4">
        <p className="text-lg font-semibold text-gray-800">{questions[current]?.blanked}</p>
      </div>
      {feedback === "wrong" && <p className="text-sm text-center mb-2 text-berry">Answer: {questions[current]?.answer}</p>}
      <div className="flex gap-2">
        <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && checkAnswer()}
          placeholder="Type the missing word..." className={`flex-1 px-4 py-3 border-2 rounded-xl text-sm focus:outline-none ${feedback === "correct" ? "border-sage bg-sage-light" : feedback === "wrong" ? "border-berry-light bg-berry-light" : "border-gray-200 focus:border-gold-500"}`} />
        <button onClick={checkAnswer} className="bg-ink-900 text-ink-900 px-5 py-3 rounded-xl font-semibold hover:bg-ink-900 transition-all">
          <Check className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

// Main Games Hub — original + new kid-friendly games
const GAMES = [
  // 🎯 NEW kid-friendly games
  { id: "vowel-catcher", title: "Vowel Catcher", desc: "Catch falling A E I O U letters!", icon: "⭐", color: "from-gold-500 to-gold-600", component: VowelCatcher, isNew: true },
  { id: "article-quiz", title: "A or An?", desc: "Pick the right article fast", icon: "📰", color: "from-sage to-sage-dark", component: ArticleQuiz, isNew: true },
  { id: "animal-sentence", title: "Animal Sentences", desc: "Build funny sentences with emojis", icon: "🦁", color: "from-rust-light to-rust-dark", component: AnimalSentence, isNew: true },
  { id: "sound-match", title: "Sound Match", desc: "Hear it, find the picture", icon: "🎧", color: "from-sky to-sky-dark", component: SoundMatch, isNew: true },
  { id: "spelling-bee", title: "Spelling Bee", desc: "Listen and type the word", icon: "🐝", color: "from-gold-300 to-gold-600", component: SpellingBee, isNew: true },
  { id: "word-hunt", title: "Word Hunt", desc: "Find hidden words in the grid", icon: "🔍", color: "from-gold-500 to-gold-600", component: WordHunt, isNew: true },
  // Original games
  { id: "match", title: "Word Match", desc: "Match words to meanings", icon: "🔗", color: "from-ink-100 to-gold-600", component: WordMatch },
  { id: "unscramble", title: "Unscramble", desc: "Rearrange jumbled letters", icon: "🔤", color: "from-rust-light to-rust-dark", component: Unscramble },
  { id: "listen", title: "Listen & Type", desc: "Type what you hear", icon: "👂", color: "from-sky to-ink-900", component: ListenType },
  { id: "fill", title: "Fill the Blank", desc: "Complete the sentence", icon: "✏️", color: "from-sage to-sage-dark", component: FillBlank },
];

export default function MiniGames() {
  const [activeGame, setActiveGame] = useState(null);
  const [result, setResult] = useState(null);

  function handleFinish(score) {
    setResult(score);
    earnXP(score * 5, "lesson");
  }

  function reset() { setActiveGame(null); setResult(null); }

  if (result !== null) {
    return (
      <div className="space-y-3 page-enter">
        <div className="bg-white rounded-2xl shadow-lg card-hover p-5 text-center">
          <div className="text-3xl mb-4">{result >= 4 ? "🎉" : result >= 2 ? "👍" : "💪"}</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Game Over!</h2>
          <p className="text-2xl font-bold text-gold-700 mb-2">{result}/5</p>
          <p className="text-gray-500 mb-2">+{result * 5} XP earned</p>
          <div className="flex gap-3 justify-center mt-3">
            <button onClick={() => { setResult(null); }} className="bg-ink-900 text-ink-900 px-6 py-3 rounded-xl font-semibold hover:bg-ink-900 transition-all">
              <RotateCcw className="w-4 h-4 inline mr-2" /> Play Again
            </button>
            <button onClick={reset} className="border border-gray-200 text-gray-700 px-6 py-3 rounded-xl font-semibold hover:bg-gray-50 transition-all">
              All Games
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (activeGame) {
    const Game = GAMES.find((g) => g.id === activeGame)?.component;
    return (
      <div className="space-y-3 page-enter">
        <button onClick={reset} className="text-gold-700 hover:text-gold-700 link-animate text-sm">&larr; All Games</button>
        <div className="bg-white rounded-2xl shadow-lg card-hover p-4">
          <Game onFinish={handleFinish} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 page-enter">
      <div>
        <Link to="/dashboard" className="text-gold-700 hover:text-gold-700 link-animate text-sm">&larr; Back to Dashboard</Link>
        <h1 className="text-2xl font-bold text-gray-900 mt-3">Mini Games</h1>
        <p className="text-gray-500 mt-1">Learn while having fun. Earn XP with every game!</p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {GAMES.map((g) => (
          <button key={g.id} onClick={() => setActiveGame(g.id)} className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${g.color || "from-ink-100 to-gold-600"} p-5 text-ink-900 text-left shadow-lg hover:shadow-2xl hover:scale-[1.03] transition-all group`}>
            {g.isNew && (
              <span className="absolute top-2 right-2 text-[9px] font-black uppercase tracking-wider bg-white text-gold-700 px-1.5 py-0.5 rounded-full shadow animate-pulse">NEW</span>
            )}
            <div className="absolute -right-4 -top-4 w-20 h-20 bg-paper-deep rounded-full blur-2xl group-hover:scale-150 transition" />
            <div className="relative">
              <div className="text-4xl mb-2 group-hover:scale-125 group-hover:rotate-6 transition-transform inline-block">{g.icon}</div>
              <h3 className="font-black text-base">{g.title}</h3>
              <p className="text-xs text-ink-700 mt-1 leading-tight">{g.desc}</p>
              <p className="text-[10px] font-bold text-gold-300 mt-2">+XP for every win</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
